import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
    deleteMessageTemplate,
    fetchAllMessageTemplates,
    fetchMessageTemplates,
    storeMessageTemplate,
    StoreMessageTemplateInput,
    updateMessageTemplate,
    UpdateMessageTemplateInput,
} from './message-template-api'
import { notification } from 'antd'
import { t } from 'i18next'
import { MessageTemplateQuery } from './message-template'

export function useMessageTemplates({
    enabled,
    preservePreviousData = true,
    query,
}: {
    enabled?: boolean
    preservePreviousData?: boolean
    query?: MessageTemplateQuery
} = {}) {
    return useQuery({
        queryKey: ['message-templates', query],
        queryFn: () => fetchMessageTemplates({ query }),
        enabled,
        placeholderData: preservePreviousData ? keepPreviousData : undefined,
    })
}

export function useAllMessageTemplates() {
    return useQuery({
        queryKey: ['message-templates', 'all'],
        queryFn: () => fetchAllMessageTemplates(),
    })
}

export function useStoreMessageTemplate() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (input: StoreMessageTemplateInput) => storeMessageTemplate(input),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['message-templates'],
            })
            notification.success({
                message: t('Template created'),
                description: t('The message template has been created successfully'),
            })
        },
    })
}

export function useUpdateMessageTemplate({ templateId }: { templateId?: string } = {}) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (input: UpdateMessageTemplateInput) => {
            return updateMessageTemplate(templateId || '', input)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['message-templates'],
            })
            notification.success({
                message: t('Template updated'),
                description: t('The message template has been updated successfully'),
            })
        },
    })
}

export function useDeleteMessageTemplate() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (templateId: string) => deleteMessageTemplate(templateId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['message-templates'],
            })
            notification.success({
                message: t('Template deleted'),
                description: t('The message template has been deleted successfully'),
            })
        },
    })
}
